import PropTypes, { instanceOf } from 'prop-types';
import React from 'react';
import {
  IoIosHome,
  IoIosPaper,
  IoIosPricetag,
  IoMdBriefcase,
  IoMdPeople,
  IoMdTime,
} from 'react-icons/io';
import { Card, Container, Flex, Grid, Spinner, Text, ThemeProvider } from 'theme-ui';

import {
  DimLoadingIndicator,
  EmployeeCard,
  HorizontalMenu,
  Layout,
  MenuRowElement,
  RefreshButton,
  SEO,
  SearchEmployeeModal,
  WorkshopInfo,
  WorkshopMap,
  WorkshopOpeningHours,
  WorkshopTypes,
} from '../../../components';
import { STRINGS } from '../../../constants';
import { Employee } from '../../../models';
import theme from '../../../theme';
import styles from './WorkshopScreen.style';

const WorkshopScreen = (props) => {
  const {
    workshop,
    workshopContact,
    workshopTypes,
    workshopOpeningHours,
    employees,
    searchEmployeesHolder,
    editActiveEmployeeHandler,
    editOwnerEmployeeHandler,
    deleteEmployeeHandler,
    selectedMenu,
    setSelectedMenu,
    currentType,
    setCurrentType,
    priceListNavigator,
    isLoading,
    isEmployeeLoading,
    status,
    // eslint-disable-next-line no-unused-vars
    isRefreshing,
    onRefresh,
    // eslint-disable-next-line no-unused-vars
    error,
  } = props;

  const renderPriceListButton = () => {
    return (
      <MenuRowElement
        action={priceListNavigator}
        text={STRINGS.workshop.priceList}
        marginHorizontal={null}
      >
        <IoIosPaper size={24} />
      </MenuRowElement>
    );
  };

  const renderWorkshop = () => {
    return (
      <Flex sx={styles.workshopContainer}>
        <Grid sx={styles.grid}>
          <WorkshopInfo workshop={workshop} contact={workshopContact} />
          <WorkshopMap workshop={workshop} />
        </Grid>

        <Grid sx={styles.grid}>
          <WorkshopTypes
            typ={workshopTypes}
            currentType={currentType}
            setCurrentType={setCurrentType}
          />
          <WorkshopOpeningHours hours={workshopOpeningHours} currentType={currentType} />
        </Grid>
        <Grid sx={styles.grid}>{renderPriceListButton()}</Grid>
      </Flex>
    );
  };

  const renderEmployees = () => {
    return (
      <Flex sx={styles.workshopContainer}>
        {isEmployeeLoading && <DimLoadingIndicator />}
        <Grid sx={styles.grid}>
          {employees.map((item) => (
            <EmployeeCard
              key={item?.id}
              data={item}
              editActiveEmployeeHandler={editActiveEmployeeHandler}
              editOwnerEmployeeHandler={editOwnerEmployeeHandler}
              deleteEmployeeHandler={deleteEmployeeHandler}
              isOwner={workshop.isOwner}
            />
          ))}
        </Grid>
      </Flex>
    );
  };

  const renderSettings = () => {
    return (
      <Flex sx={styles.workshopContainer}>
        <Flex sx={styles.settingsContainer}>
          <MenuRowElement action={null} text={STRINGS.workshop.preferredVisitTime}>
            <IoMdTime size={28} />
          </MenuRowElement>
          <MenuRowElement action={null} text={STRINGS.workshop.workshopType}>
            <IoMdBriefcase size={28} />
          </MenuRowElement>
          <MenuRowElement action={null} text={STRINGS.workshop.priceList}>
            <IoIosPricetag size={28} />
          </MenuRowElement>
          {workshop.isOwner && (
            <MenuRowElement
              action={() => searchEmployeesHolder.setVisible(true)}
              text={STRINGS.workshop.searchEmployees}
            >
              <IoMdPeople size={28} />
            </MenuRowElement>
          )}
        </Flex>
      </Flex>
    );
  };

  const renderContent = () => {
    if (!workshop?.name) {
      return (
        <Card variant="workshopCard" sx={styles.emptyCard}>
          <Flex sx={styles.emptyContainer}>
            <Text sx={styles.emptyText}>{STRINGS.workshop.noWorkshopText}</Text>
            <IoIosHome size={32} />
          </Flex>
        </Card>
      );
    }
    return (
      <Card variant="workshopCard" sx={styles.card}>
        <Flex sx={styles.menuContainer}>
          <HorizontalMenu
            labels={[
              STRINGS.workshop.menuInfo,
              STRINGS.workshop.menuEmployees,
              STRINGS.workshop.menuSettings,
            ]}
            setSelectedMenu={setSelectedMenu}
            selectedMenu={selectedMenu}
          >
            {renderWorkshop()}
            {renderEmployees()}
            {renderSettings()}
          </HorizontalMenu>
        </Flex>
      </Card>
    );
  };

  if (status === 'authenticated' && !isLoading) {
    return (
      <ThemeProvider theme={theme}>
        <Layout>
          <SEO title={workshop?.name} />
          <Container sx={styles.container}>
            <RefreshButton onRefresh={onRefresh} isRefreshing={isRefreshing} />
            <SearchEmployeeModal searchEmployeesHolder={searchEmployeesHolder} />
            {renderContent()}
            {isLoading && (
              <Flex sx={styles.loadingIndicator}>
                <Spinner />
              </Flex>
            )}
          </Container>
        </Layout>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Layout>
        <SEO title={STRINGS.seo.loading} />
        <Container sx={styles.container}>
          <Flex sx={styles.spinnerContainer}>
            <Spinner size={60} />
          </Flex>
        </Container>
      </Layout>
    </ThemeProvider>
  );
};

export default WorkshopScreen;

WorkshopScreen.propTypes = {
  workshop: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
  workshopContact: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
  workshopOpeningHours: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
  isLoading: PropTypes.bool.isRequired,
  isEmployeeLoading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  editActiveEmployeeHandler: PropTypes.func.isRequired,
  editOwnerEmployeeHandler: PropTypes.func.isRequired,
  deleteEmployeeHandler: PropTypes.func.isRequired,
  employees: PropTypes.arrayOf(instanceOf(Employee)).isRequired,
  selectedMenu: PropTypes.number.isRequired,
  setSelectedMenu: PropTypes.func.isRequired,
  currentType: PropTypes.string,
  setCurrentType: PropTypes.func.isRequired,
};

WorkshopScreen.defaultProps = {
  error: undefined,
  currentType: null,
};
